import React, { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Calendar, FileText, BarChart3, PieChart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { hasPermission, formatCurrency, formatCurrencyForPDF, formatDate, parseAmount } from '../../utils/helpers';
import Button from '../common/Button';
import { BarChart, Bar, PieChart as RePieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LOGO_BASE64 } from '../../utils/logoBase64';
import { logger, performance, logFirebaseError } from '../../utils/logger';


const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const FinancialReports = () => {
  const { currentUser } = useAuth();
  const { data } = useData();
  const [reportType, setReportType] = useState('monthly'); // 'monthly', 'annual', 'custom'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const canView = hasPermission(currentUser, 'view_finances') || hasPermission(currentUser, 'view_all');
  const canExport = hasPermission(currentUser, 'export_finances') || hasPermission(currentUser, 'export_all');

  if (!canView) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Accès restreint</h3>
        <p className="text-gray-600 dark:text-gray-400">Vous n'avez pas la permission de voir les rapports financiers.</p>
      </div>
    );
  }

  // Filtrer les données selon la période
  const getFilteredData = () => {
    let donations = data.donations || [];
    let expenses = data.expenses || [];

    if (reportType === 'monthly') {
      donations = donations.filter(d => {
        if (!d.date) return false;
        const date = new Date(d.date);
        return !isNaN(date.getTime()) && date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
      });
      expenses = expenses.filter(e => {
        if (!e.date) return false;
        const date = new Date(e.date);
        return !isNaN(date.getTime()) && date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
      });
    } else if (reportType === 'annual') {
      donations = donations.filter(d => {
        if (!d.date) return false;
        const date = new Date(d.date);
        return !isNaN(date.getTime()) && date.getFullYear() === selectedYear;
      });
      expenses = expenses.filter(e => {
        if (!e.date) return false;
        const date = new Date(e.date);
        return !isNaN(date.getTime()) && date.getFullYear() === selectedYear;
      });
    } else if (reportType === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      donations = donations.filter(d => {
        if (!d.date) return false;
        const date = new Date(d.date);
        return !isNaN(date.getTime()) && date >= start && date <= end;
      });
      expenses = expenses.filter(e => {
        if (!e.date) return false;
        const date = new Date(e.date);
        return !isNaN(date.getTime()) && date >= start && date <= end;
      });
    }

    return { donations, expenses };
  };

  const { donations, expenses } = getFilteredData();

  // Calculs financiers
  const totalDonations = donations.reduce((sum, d) => sum + parseAmount(d.amount), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + parseAmount(e.amount), 0);
  const balance = totalDonations - totalExpenses;

  // Données par type de revenu
  const donationsByType = donations.reduce((acc, d) => {
    const type = d.type || 'Autre';
    acc[type] = (acc[type] || 0) + parseAmount(d.amount);
    return acc;
  }, {});

  const donationsChartData = Object.entries(donationsByType).map(([type, amount]) => ({
    name: type,
    value: amount
  }));

  // Données par catégorie de dépense
  const expensesByCategory = expenses.reduce((acc, e) => {
    const category = e.category || 'Autre';
    acc[category] = (acc[category] || 0) + parseAmount(e.amount);
    return acc;
  }, {});

  const expensesChartData = Object.entries(expensesByCategory).map(([category, amount]) => ({
    name: category,
    value: amount
  }));

  // Données mensuelles pour graphique en ligne (si rapport annuel)
  const getMonthlyData = () => {
    const monthlyData = [];
    for (let i = 0; i < 12; i++) {
      const monthDonations = (data.donations || []).filter(d => {
        if (!d.date) return false;
        const date = new Date(d.date);
        return !isNaN(date.getTime()) && date.getMonth() === i && date.getFullYear() === selectedYear;
      }).reduce((sum, d) => sum + parseAmount(d.amount), 0);

      const monthExpenses = (data.expenses || []).filter(e => {
        if (!e.date) return false;
        const date = new Date(e.date);
        return !isNaN(date.getTime()) && date.getMonth() === i && date.getFullYear() === selectedYear;
      }).reduce((sum, e) => sum + parseAmount(e.amount), 0);

      monthlyData.push({
        month: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'][i],
        revenus: monthDonations,
        depenses: monthExpenses,
        solde: monthDonations - monthExpenses
      });
    }
    return monthlyData;
  };

  const getPeriodLabel = () => {
    if (reportType === 'monthly') {
      const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      return `${monthNames[selectedMonth]} ${selectedYear}`;
    } else if (reportType === 'annual') {
      return `Année ${selectedYear}`;
    } else {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
  };

  const exportDetailedPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Logo
    try {
      doc.addImage(LOGO_BASE64, 'JPEG', pageWidth / 2 - 20, 10, 40, 20);
    } catch (e) {
      logger.warn('Erreur lors de l\'ajout du logo:', e);
    }
    
    // En-tête
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT FINANCIER DÉTAILLÉ', pageWidth / 2, 38, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('VOMM FJKM Hannover', pageWidth / 2, 46, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Période: ${getPeriodLabel()}`, pageWidth / 2, 53, { align: 'center' });
    doc.text(`Date de génération: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, 59, { align: 'center' });
    
    // Ligne de séparation
    doc.setLineWidth(0.5);
    doc.line(15, 63, pageWidth - 15, 63);
    
    // Résumé financier
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('RÉSUMÉ FINANCIER', 15, 73);
    
    autoTable(doc, {
      startY: 78,
      head: [['Catégorie', 'Montant', 'Transactions']],
      body: [
        ['Total Revenus', formatCurrencyForPDF(totalDonations), `${donations.length} transactions`],
        ['Total Dépenses', formatCurrencyForPDF(totalExpenses), `${expenses.length} transactions`],
        ['Solde', formatCurrencyForPDF(balance), balance >= 0 ? 'Excédent' : 'Déficit']
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      bodyStyles: { fontSize: 11 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { halign: 'right', cellWidth: 50 },
        2: { halign: 'center', cellWidth: 60 }
      }
    });
    
    let yPos = doc.lastAutoTable.finalY + 15;
    
    // Détail des revenus
    if (donations.length > 0) {
      // Vérifier si on a besoin d'une nouvelle page
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('DÉTAIL DES REVENUS', 15, yPos);
      
      autoTable(doc, {
        startY: yPos + 5,
        head: [['Date', 'Référence', 'Type', 'Montant', 'Méthode', 'Libellé']],
        body: donations.map(d => [
          formatDate(d.date),
          d.reference || '-',
          d.type || '-',
          formatCurrencyForPDF(d.amount),
          d.method || '-',
          (d.libelle || '-').substring(0, 30)
        ]),
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 48 }
        }
      });
      
      // Total des revenus
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY,
        body: [['TOTAL REVENUS', '', '', formatCurrencyForPDF(totalDonations), '', '']],
        theme: 'plain',
        bodyStyles: { fontSize: 9, fontStyle: 'bold', fillColor: [220, 252, 231] },
        columnStyles: {
          3: { halign: 'right', cellWidth: 25 }
        }
      });
    }
    
    // Détail des dépenses
    if (expenses.length > 0) {
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Vérifier si on a besoin d'une nouvelle page
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('DÉTAIL DES DÉPENSES', 15, yPos);
      
      autoTable(doc, {
        startY: yPos + 5,
        head: [['Date', 'Référence', 'Catégorie', 'Montant', 'Statut', 'Libellé']],
        body: expenses.map(e => [
          formatDate(e.date),
          e.reference || '-',
          e.category || '-',
          formatCurrencyForPDF(e.amount),
          e.status || '-',
          (e.libelle || '-').substring(0, 30)
        ]),
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 48 }
        }
      });
      
      // Total des dépenses
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY,
        body: [['TOTAL DÉPENSES', '', '', formatCurrencyForPDF(totalExpenses), '', '']],
        theme: 'plain',
        bodyStyles: { fontSize: 9, fontStyle: 'bold', fillColor: [254, 226, 226] },
        columnStyles: {
          3: { halign: 'right', cellWidth: 25 }
        }
      });
    }
    
    // Pied de page sur chaque page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Page ${i} sur ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`Rapport_Detaille_${getPeriodLabel()}.pdf`);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rapports Financiers</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Analyse et statistiques financières</p>
        </div>
        {canExport && (
          <Button
            onClick={exportDetailedPDF}
            icon={FileText}
            variant="success"
          >
            Exporter PDF
          </Button>
        )}
      </div>

      {/* Sélection de période */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type de rapport
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="monthly">Mensuel</option>
              <option value="annual">Annuel</option>
              <option value="custom">Personnalisé</option>
            </select>
          </div>

          {reportType === 'monthly' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mois
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Année
                </label>
                <input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </>
          )}

          {reportType === 'annual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Année
              </label>
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {reportType === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date début
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date fin
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Cartes de résumé */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenus</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-500 mt-2">
                {formatCurrency(totalDonations)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {donations.length} transactions
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Dépenses</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-500 mt-2">
                {formatCurrency(totalExpenses)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {expenses.length} transactions
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <TrendingDown className="w-8 h-8 text-red-600 dark:text-red-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Solde</p>
              <p className={`text-3xl font-bold mt-2 ${balance >= 0 ? 'text-blue-600 dark:text-blue-500' : 'text-red-600 dark:text-red-500'}`}>
                {formatCurrency(balance)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {balance >= 0 ? 'Excédent' : 'Déficit'}
              </p>
            </div>
            <div className={`p-3 rounded-full ${balance >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              <DollarSign className={`w-8 h-8 ${balance >= 0 ? 'text-blue-600 dark:text-blue-500' : 'text-red-600 dark:text-red-500'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenus par type */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenus par type</h3>
          {donationsChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RePieChart>
                <Pie
                  data={donationsChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {donationsChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </RePieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Aucune donnée pour cette période
            </div>
          )}
        </div>

        {/* Dépenses par catégorie */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dépenses par catégorie</h3>
          {expensesChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={expensesChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
      </div>

      {/* Évolution mensuelle (pour rapport annuel) */}
      {reportType === 'annual' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Évolution mensuelle</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenus" stroke="#10B981" strokeWidth={2} name="Revenus" />
              <Line type="monotone" dataKey="depenses" stroke="#EF4444" strokeWidth={2} name="Dépenses" />
              <Line type="monotone" dataKey="solde" stroke="#3B82F6" strokeWidth={2} name="Solde" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Détails des transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Revenus */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 5 Revenus</h3>
          <div className="space-y-3">
            {donations
              .sort((a, b) => b.amount - a.amount)
              .slice(0, 5)
              .map((donation) => (
                <div key={donation.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{donation.libelle}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(donation.date)} • {donation.type}</p>
                  </div>
                  <p className="text-lg font-bold text-green-600 dark:text-green-500">{formatCurrency(donation.amount)}</p>
                </div>
              ))}
            {donations.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">Aucune transaction</p>
            )}
          </div>
        </div>

        {/* Top Dépenses */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top 5 Dépenses</h3>
          <div className="space-y-3">
            {expenses
              .sort((a, b) => b.amount - a.amount)
              .slice(0, 5)
              .map((expense) => (
                <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{expense.libelle}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(expense.date)} • {expense.category}</p>
                  </div>
                  <p className="text-lg font-bold text-red-600 dark:text-red-500">{formatCurrency(expense.amount)}</p>
                </div>
              ))}
            {expenses.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">Aucune transaction</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReports;